/**
 * BarcodeScanner — ONE camera scanner for the whole app.
 *
 * ⛔⛔ THE BACK CAMERA IS THE WHOLE POINT. A collector scans a barcode on a slab or a bag held in
 * front of them. The Vault Scan tab expressed NO `facingMode` at all, so the camera was whatever
 * `enumerateDevices()` happened to list first plus whatever `rememberLastUsedCamera` had persisted
 * in localStorage — on a phone that commonly means the SELFIE camera, pointed at the collector's
 * face, with no reliable way back. A scanner that opens on the wrong camera is not a degraded
 * scanner, it is a non-functional one.
 *
 * ⛔ TWO IMPLEMENTATIONS IS THE ACTUAL BUG. Vault Scan used the full-UI `Html5QrcodeScanner` (library
 * chrome, its own camera dropdown) while admin ingestion used headless `Html5Qrcode` with
 * `facingMode: 'environment'`. One was right, one was wrong, and a fix applied to either left the
 * other broken. This component exists so there is exactly one camera implementation to get right.
 *
 * ⛔⛔ EVERY TRANSITION IS SERIALISED, BECAUSE THE CAMERA IS A SINGLE EXCLUSIVE RESOURCE. `start()`
 * and `stop()` are async and the browser grants one video track at a time. The old code called
 * `clear()` from a SYNCHRONOUS React cleanup and never awaited it, so a remount (tab switch, a
 * re-render, a double-tapped flip) raced a still-closing camera and the new scanner came up black.
 * `runExclusive` chains every start/stop through one promise so two can never overlap — this is the
 * fix for the flip button, the remount race, and the teardown race, all at once.
 *
 * ⚠ THE PARENT'S CALLBACKS DO NOT RE-INIT THE CAMERA. They are held in refs and the init effect
 * depends only on the facing mode. The admin scanner listed `onError` in its dep array, so an
 * unmemoised inline prop re-ran the effect on every parent render — restarting the camera in a loop.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Camera, Lightbulb, LightbulbOff, RefreshCw, VideoOff } from 'lucide-react';

export const FACING_ENVIRONMENT = 'environment';
export const FACING_USER = 'user';

/** The formats a comic barcode actually uses. Narrowing the set speeds up per-frame decoding. */
export const SUPPORTED_FORMAT_NAMES = ['UPC_A', 'UPC_E', 'EAN_13', 'EAN_8', 'CODE_128', 'CODE_39'];

/** A permission refusal is a DIFFERENT problem from a missing camera, and needs different words. */
export function classifyCameraError(err) {
  const name = err?.name || '';
  const msg = String(err?.message || err || '');
  if (name === 'NotAllowedError' || /permission|denied/i.test(msg)) return 'permission';
  if (name === 'NotFoundError' || /no camera|not found|devices/i.test(msg)) return 'nocamera';
  return 'other';
}

const MESSAGES = {
  permission: 'Camera access was blocked. Allow the camera in your browser settings, or use Search instead.',
  nocamera: 'No camera was found on this device. Use Search, or type the barcode there.',
  other: 'The camera scanner could not start. Use Search, or type the barcode there.',
};

export default function BarcodeScanner({
  onScan,
  onError,
  defaultFacing = FACING_ENVIRONMENT,
  showFlip = true,
  paused = false,
  className = '',
}) {
  // ⚠ A HARDCODED DOM ID CANNOT BE SHARED. The admin scanner hardcoded 'scanner-container', so two
  // instances on one page would both hand the same node to the library and fight over it.
  const reactId = useId();
  const containerId = `bcscan-${reactId.replace(/[^a-zA-Z0-9]/g, '')}`;

  const [facing, setFacing] = useState(defaultFacing);
  const [status, setStatus] = useState('starting'); // starting | running | error
  const [errorKind, setErrorKind] = useState(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);

  const scannerRef = useRef(null);
  const disposedRef = useRef(false);
  const chainRef = useRef(null);
  const lastScanRef = useRef(null);
  const onScanRef = useRef(onScan);
  const onErrorRef = useRef(onError);

  // Callbacks live in refs so a parent re-render never restarts the camera.
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  if (chainRef.current === null) chainRef.current = Promise.resolve();

  /** Serialise every camera transition. Two `start()`s can never be in flight at once. */
  const runExclusive = useCallback((task) => {
    const next = chainRef.current.then(task, task);
    chainRef.current = next.then(() => undefined, () => undefined);
    return next;
  }, []);

  const stopInner = useCallback(async () => {
    const inst = scannerRef.current;
    if (!inst) return;
    scannerRef.current = null;
    try {
      // ⛔ AWAITED, unlike the two implementations this replaces. The next start must not begin
      // until the video track is actually released.
      await inst.stop();
    } catch {
      // Already stopped, or the node is gone. Nothing left to release.
    }
    try {
      inst.clear?.();
    } catch {
      // clear() throws when the viewfinder was already torn down.
    }
  }, []);

  const startInner = useCallback(async (nextFacing) => {
    setStatus('starting');
    setErrorKind(null);
    setTorchAvailable(false);
    setTorchOn(false);
    try {
      const mod = await import('html5-qrcode');
      const { Html5Qrcode, Html5QrcodeSupportedFormats } = mod;
      // The only place disposal can land is across this await. A container node that has gone
      // missing makes the library throw, which the catch below turns into the error affordance.
      if (disposedRef.current) return;

      const inst = new Html5Qrcode(containerId);
      scannerRef.current = inst;

      const formats = SUPPORTED_FORMAT_NAMES
        .map((n) => Html5QrcodeSupportedFormats?.[n])
        .filter((v) => v !== undefined);

      await inst.start(
        { facingMode: nextFacing },
        {
          fps: 10,
          qrbox: { width: 250, height: 120 },
          ...(formats.length ? { formatsToSupport: formats } : {}),
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        },
        (decodedText, decodedResult) => {
          // Debounce: a live viewfinder re-decodes the same barcode many times a second.
          if (lastScanRef.current === decodedText) return;
          lastScanRef.current = decodedText;
          setTimeout(() => {
            if (lastScanRef.current === decodedText) lastScanRef.current = null;
          }, 2000);
          if (navigator.vibrate) navigator.vibrate(100);
          const format = decodedResult?.result?.format?.formatName || 'UNKNOWN';
          onScanRef.current?.(decodedText, format);
        },
        () => {
          // Per-frame decode misses are the normal state of a live viewfinder.
        }
      );

      if (disposedRef.current) {
        await stopInner();
        return;
      }
      setStatus('running');

      try {
        // ⛔ torchFeature is a METHOD, not a property (html5-qrcode/camera/core.d.ts:19).
        // The admin scanner this replaces read `capabilities?.torchFeature?.isSupported?.()` --
        // a property lookup on a function, which is always undefined, so its torch button could
        // never appear on any device. Calling it is what makes the capability check real.
        if (inst.getRunningTrackCameraCapabilities().torchFeature().isSupported()) {
          setTorchAvailable(true);
        }
      } catch {
        // Torch is an enhancement; its absence is not an error.
      }
    } catch (err) {
      scannerRef.current = null;
      if (disposedRef.current) return;
      setErrorKind(classifyCameraError(err));
      setStatus('error');
      onErrorRef.current?.(err);
    }
  }, [containerId, stopInner]);

  // Init + teardown. Depends ONLY on `facing` — never on parent callbacks.
  useEffect(() => {
    disposedRef.current = false;
    runExclusive(() => startInner(facing));
    return () => {
      disposedRef.current = true;
      // Cleanup is synchronous, so the stop is QUEUED on the same chain rather than fired loose.
      // A remount's start() sits behind it and cannot race a closing camera.
      runExclusive(() => stopInner());
    };
  }, [facing, runExclusive, startInner, stopInner]);

  // Pause/resume carried over from the admin scanner so the extraction loses no capability.
  // Guarded on `status` because pause() throws when the camera is not actually running.
  useEffect(() => {
    const inst = scannerRef.current;
    if (!inst || status !== 'running') return;
    try {
      if (paused) inst.pause();
      else inst.resume();
    } catch {
      // A scanner that refuses to pause must not take the viewfinder down.
    }
  }, [paused, status]);

  const flip = useCallback(() => {
    // The button only moves state; the effect performs the serialised stop -> start.
    setFacing((f) => (f === FACING_ENVIRONMENT ? FACING_USER : FACING_ENVIRONMENT));
  }, []);

  const toggleTorch = useCallback(async () => {
    try {
      // No null-guard on the ref: mid-flip it is briefly null, and reading through it then throws
      // a TypeError that this same catch swallows -- identical outcome, one less way to be wrong.
      await scannerRef.current.getRunningTrackCameraCapabilities().torchFeature().apply(!torchOn);
      setTorchOn((v) => !v);
    } catch {
      // A torch that refuses to toggle must not take the scanner down.
    }
  }, [torchOn]);

  return (
    <div className={`relative ${className}`} data-testid="barcode-scanner">
      <div
        id={containerId}
        data-testid="barcode-scanner-viewport"
        data-facing={facing}
        className="w-full bg-black rounded-xl overflow-hidden"
        style={{ minHeight: '280px' }}
      />

      {/* Branded viewfinder — Maestro zinc/orange, not the library's chrome. */}
      {status === 'running' && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="relative w-64 h-28 border-2 border-orange-500/50 rounded-lg">
            <div className="absolute -top-1 -left-1 w-4 h-4 border-t-2 border-l-2 border-orange-500" />
            <div className="absolute -top-1 -right-1 w-4 h-4 border-t-2 border-r-2 border-orange-500" />
            <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b-2 border-l-2 border-orange-500" />
            <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-2 border-r-2 border-orange-500" />
            <div className="absolute inset-x-2 h-0.5 bg-orange-500 animate-scan-line" />
          </div>
        </div>
      )}

      {paused && status === 'running' && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-xl" data-testid="scanner-paused">
          <p className="text-white font-medium">Scanner paused</p>
        </div>
      )}

      {/* ⛔ A BLACK FRAME MUST NEVER BE SILENT. */}
      {status === 'starting' && (
        <div className="absolute inset-0 bg-zinc-900 flex items-center justify-center rounded-xl" data-testid="scanner-starting">
          <div className="text-center">
            <Camera className="w-12 h-12 text-orange-500 mx-auto mb-2 animate-pulse" />
            <p className="text-zinc-400">Starting camera…</p>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="absolute inset-0 bg-zinc-900 flex items-center justify-center rounded-xl p-4" data-testid="scanner-error">
          <div className="text-center">
            <VideoOff className="w-10 h-10 text-yellow-400 mx-auto mb-2" />
            <p className="text-yellow-400 text-sm" data-testid={`scanner-error-${errorKind}`}>
              {MESSAGES[errorKind]}
            </p>
          </div>
        </div>
      )}

      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
        {showFlip && (
          <button
            type="button"
            onClick={flip}
            data-testid="scanner-flip"
            aria-label={facing === FACING_ENVIRONMENT ? 'Switch to front camera' : 'Switch to back camera'}
            className="p-3 bg-zinc-800/80 backdrop-blur rounded-full hover:bg-zinc-700/80 transition-colors"
          >
            <RefreshCw className="w-5 h-5 text-white" />
          </button>
        )}
        {torchAvailable && (
          <button
            type="button"
            onClick={toggleTorch}
            data-testid="scanner-torch"
            aria-label={torchOn ? 'Turn off flashlight' : 'Turn on flashlight'}
            className={`p-3 rounded-full transition-colors ${
              torchOn ? 'bg-yellow-500/80 hover:bg-yellow-400/80' : 'bg-zinc-800/80 backdrop-blur hover:bg-zinc-700/80'
            }`}
          >
            {torchOn ? <LightbulbOff className="w-5 h-5 text-zinc-900" /> : <Lightbulb className="w-5 h-5 text-white" />}
          </button>
        )}
      </div>

      <style>{`
        @keyframes scanLine { 0% { top: 0; } 50% { top: calc(100% - 2px); } 100% { top: 0; } }
        .animate-scan-line { animation: scanLine 2s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
